import React, { useState, useEffect } from "react"
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Row,
  Col,
  Button,
  Alert
} from "reactstrap"
import { useMutation } from "@apollo/react-hooks"
import { useQueryData } from "utils"
import { withRouter } from "react-router"
import { flowRight as compose } from "lodash"
import { withBreadcrumb, useBread } from "components/breadcrumb"
import { withBasicNotify } from "components/notification"
import { Spinner, BarLoader } from "components/spinner"
import withModal from "components/modal"
import DetailItemList from "components/order-detail-item-list"
import DetailItem from "components/back-order-detail-item"
import OrderForm from "./OrderForm"
import { GET_ORDER, UPDATE_ORDER, UPDATE_ORDER_DETAILS } from "../gql"

document.body.scrollTop = 0 // For Safari
document.documentElement.scrollTop = 0 // For Chrome, Firefox, IE and Opera

const OrderContainer = (props) => {
  const orderId = props.match.params.id

  const { data, loading, error } = useQueryData(GET_ORDER, {
    skip: !orderId,
    variables: { id: orderId },
    fetchPolicy: "cache-and-network"
  })

  const [status, setStatus] = useState(data?.order?.status || "")
  const [orderDetails, setOrderDetails] = useState(
    data?.order?.orderDetails || []
  )
  const [invoiceFile, setInvoiceFile] = useState(null)
  const [expectedDate, setExpectedDate] = useState(data?.order?.expectedDate)
  const [invoice, setInvoice] = useState(data?.order?.invoiceNumber)
  const [delivery, setDelivery] = useState(data?.order?.deliveryNumber)
  const [deliveryType, setDeliveryType] = useState(data?.order?.deliveryType)

  useEffect(() => {
    if (data?.order?.deliveryType) {
      setDeliveryType(data?.order?.deliveryType)
    }
  }, [data])

  const [
    updateOrder,
    { loading: updateOrderLoading, error: updateOrderError }
  ] = useMutation(UPDATE_ORDER)

  const [
    updateOrderDetails,
    { loading: updateOrderDetailLoading, error: updateOrderDetailError }
  ] = useMutation(UPDATE_ORDER_DETAILS, {
    variables: {
      orderId: props.match.params.id,
      updateOrderDetailInputs:
        orderDetails?.length > 0
          ? orderDetails?.map((orderDetail) => {
              return {
                ...orderDetail,
                __typename: undefined,
                product: undefined
              }
            })
          : []
    }
  })

  useBread({ to: props.match.url, name: "訂單維護" })
  useStatus(setStatus, data)
  useInvoiceNumber(setInvoice, data)
  useDeliveryNumber(setDelivery, data)
  useExpectedDate(setExpectedDate, data)
  useOrderDetails(setOrderDetails, data)

  const orderStatus = data?.order?.status

  // const checkRequire = () => {
  //   return status === "" || (status === "CONFIRMED" && !expectedDate)
  // }

  const variableFromData = () => {
    return {
      id: orderId,
      input: {
        status,
        expectedDate: expectedDate ? new Date(expectedDate) : undefined,
        invoiceNumber: invoice,
        deliveryNumber: delivery,
        deliveryType,
        file: invoiceFile
      }
    }
  }

  // const checkOrderDetails = () => {
  //   return (
  //     orderDetails?.filter(
  //       (o) =>
  //         o.unitPrice === undefined || o.unitPrice === null || o.unitPrice === 0
  //     ).length !== 0
  //   )
  // }

  const showAlertModal = (callBack) => {
    props.modal.setCentered(false)
    props.modal.setModalSize("md")
    props.modal.setHeaderText("系統提示")
    props.modal.setModalContent("請再次確認品項、數量及金額是否正確")
    props.modal.setOnSubmit(() => {
      callBack()
    })
    props.modal.toggle()
  }

  if (loading || !data) {
    return <Spinner />
  }

  if (error) {
    return <Alert color="danger">錯誤訊息：{`${error}`}</Alert>
  }

  return (
    <div>
      <Card>
        <OrderForm
          data={data}
          orderStatus={orderStatus}
          status={status}
          setStatus={setStatus}
          expectedDate={expectedDate}
          setExpectedDate={setExpectedDate}
          invoice={invoice}
          setInvoice={setInvoice}
          delivery={delivery}
          setDelivery={setDelivery}
          deliveryType={deliveryType}
          setDeliveryType={setDeliveryType}
          invoiceUrl={data?.order?.invoiceUrl}
          setInvoiceFile={setInvoiceFile}
        />
        <Button
          color="warning"
          style={{ width: "120px" }}
          onClick={async () => {
            await updateOrder({
              variables: variableFromData()
            })

            props.notify.success("訂單更新成功")

            props.history.goBack()
          }}
          className="ml-3 mr-3 mt-0 mb-3"
        >
          更新訂單資訊
        </Button>
        <CardHeader>
          <Row style={{ display: "flex", alignItems: "center" }}>
            <Col>
              <i className="mdi mdi-information-outline mr-2"> </i> 商品資訊
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <DetailItemList
            renderItems={() =>
              orderDetails?.map((orderDetail) => (
                <DetailItem
                  key={orderDetail.id}
                  setOrderDetails={setOrderDetails}
                  orderDetails={orderDetails}
                  orderDetail={orderDetail}
                  isAssistant={true}
                  readOnly={
                    data.order.status === "CANCEL" ||
                    data.order.status === "CONFIRMED" ||
                    data.order.status === "SUCCESS"
                  }
                />
              ))
            }
          />
        </CardBody>
        <CardFooter>
          <Row style={{ display: "flex", alignItems: "center" }}>
            <Col>
              <Button
                color="primary"
                onClick={() => {
                  showAlertModal(() => {
                    updateOrderDetails()
                    props.notify.success("訂單明細更新成功")
                  })
                }}
                disabled={
                  data.order.status === "CONFIRMED" ||
                  data.order.status === "SUCCESS" ||
                  data.order.status === "CANCEL"
                }
              >
                更新訂單明細
              </Button>
            </Col>
          </Row>
        </CardFooter>
        <BarLoader loading={updateOrderLoading || updateOrderDetailLoading} />
      </Card>
      {(updateOrderError || updateOrderDetailError) && (
        <Alert color="danger">
          錯誤訊息：{`${updateOrderError.message || updateOrderDetailError}`}
        </Alert>
      )}
    </div>
  )
}

const useStatus = (setStatus, data) => {
  useEffect(() => {
    setStatus(data?.order?.status || "")
  }, [setStatus, data])
}

const useExpectedDate = (setExpectedDate, data) => {
  useEffect(() => {
    setExpectedDate(data?.order?.expectedDate)
  }, [setExpectedDate, data])
}

const useInvoiceNumber = (setInvoice, data) => {
  useEffect(() => {
    setInvoice(data?.order?.invoiceNumber)
  }, [setInvoice, data])
}

const useDeliveryNumber = (setDelivery, data) => {
  useEffect(() => {
    setDelivery(data?.order?.deliveryNumber)
  }, [data, setDelivery])
}

const useOrderDetails = (setOrderDetails, data) => {
  useEffect(() => {
    setOrderDetails(data?.order?.orderDetails)
  }, [setOrderDetails, data])
}

export default compose(
  withRouter,
  withBreadcrumb,
  withBasicNotify,
  withModal
)(OrderContainer)
